import { CtaButton } from '@tkww/orion-web-ds-action';
import { Input } from '@tkww/orion-web-ds-input';
import { TextBody } from '@tkww/orion-web-ds-text';
import React, { useState } from 'react';
import { decisionArea } from '../../../hooks/trackingConstants';
import { useTrackEstimateStepNext } from '../../../hooks/useTrackEstimateStepNext';
import {
	DIGITS_ONLY_PATTERN,
	getGuestCountError,
} from '../../../utils/guestCount';
import Styles from './styles.scss';

type OrionInputChangeEvent = {
	target: {
		value: string;
	};
};

export interface GuestCountStepProps {
	guestCount: string;
	onChange: (value: string) => void;
	onNext: () => void;
	buttonLabel?: string;
}

export const GuestCountStep = ({
	guestCount,
	onChange,
	onNext,
	buttonLabel = 'Next',
}: GuestCountStepProps) => {
	const [error, setError] = useState<string | undefined>(undefined);
	const trackGuestCountNext = useTrackEstimateStepNext();

	const handleChange = (e: OrionInputChangeEvent) => {
		const value = e.target.value;
		if (value !== '' && !DIGITS_ONLY_PATTERN.test(value)) return;
		setError(undefined);
		onChange(value);
	};

	const validate = () => {
		const err = getGuestCountError(guestCount);
		if (err) {
			setError(err);
			return false;
		}
		return true;
	};

	const handleNext = () => {
		if (!validate()) return;
		trackGuestCountNext(decisionArea.GUEST_COUNT, guestCount);
		onNext();
	};

	return (
		<div className={Styles.guestCountStep} data-testid="guest-count-step">
			<div className={Styles.header}>
				<TextBody size="xl" bold>
					How many guests are joining you?
				</TextBody>
				<TextBody size="sm" color="subtle-dark">
					We need an exact number to calculate your estimate. Give your best
					guess—you can update this later.
				</TextBody>
			</div>
			<div className={Styles.inputArea}>
				<Input
					label="Guest count"
					name="guestCount"
					type="text"
					inputMode="numeric"
					icon="group"
					value={guestCount}
					onChange={handleChange}
					onBlur={validate}
					state={error ? 'invalid' : 'neutral'}
					subText={error}
					data-testid="guest-count-input"
				/>
			</div>
			<CtaButton
				className={Styles.ctaButton}
				styleType="primary"
				size="lg"
				onClick={handleNext}
				disabled={!!getGuestCountError(guestCount)}
				data-testid="guest-count-next-button"
			>
				{buttonLabel}
			</CtaButton>
		</div>
	);
};
